import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Store, Tag, Download, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShareDeal() {
  const { dealId } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  // Capture referral code from URL
  const refCode = new URLSearchParams(window.location.search).get("ref");

  useEffect(() => {
    // Store referral code for signup
    if (refCode) {
      localStorage.setItem("referral_code", refCode);
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/community/deals/${dealId}/public`);
        if (res.ok) {
          const data = await res.json();
          setDeal(data.deal);
        }
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [dealId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Deal Not Found</h1>
        <p className="text-sm text-gray-500 mb-6">This deal may have been removed or expired.</p>
        <Button onClick={() => window.location.href = "/"} className="bg-green-500 hover:bg-green-600 text-white">
          <Download className="h-4 w-4 mr-2" /> Get Foodz Lizts
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white" data-testid="share-deal-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-4 text-center">
        <h1 className="text-lg font-bold text-white">Foodz Lizts</h1>
        <p className="text-green-100 text-xs">Your All in One Grocery Budget Shopping Assistant</p>
      </div>

      {/* Deal Card */}
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {/* Posted by */}
          <div className="flex items-center gap-2 px-4 pt-4 pb-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold">
              {(deal.posted_by || "A")[0].toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-800">{deal.posted_by || "Community Member"}</p>
              <p className="text-xs text-gray-400">Shared on Foodz Lizts</p>
            </div>
            {deal.verified && (
              <Badge className="bg-green-100 text-green-700 text-xs">
                <ShieldCheck className="h-3 w-3 mr-0.5" />Verified
              </Badge>
            )}
          </div>

          {/* Image */}
          {deal.image_url && (
            <div className="px-4 py-2">
              <img
                src={deal.image_url}
                alt={deal.title}
                className="w-full h-56 object-contain bg-gray-50 rounded-xl"
              />
            </div>
          )}

          {/* Details */}
          <div className="px-4 pb-4">
            <h2 className="text-lg font-bold text-gray-900 leading-snug">
              {deal.title || deal.product_name || deal.description}
            </h2>
            {deal.description && deal.description !== deal.title && (
              <p className="text-sm text-gray-500 mt-1">{deal.description}</p>
            )}

            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {deal.store_name && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Store className="h-3 w-3" />{deal.store_name}
                </span>
              )}
              {deal.category && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Tag className="h-3 w-3" />{deal.category}
                </span>
              )}
            </div>

            <div className="flex items-baseline gap-3 mt-3">
              {deal.price != null && (
                <span className="text-2xl font-bold text-green-600">${deal.price.toFixed(2)}</span>
              )}
              {deal.original_price != null && deal.original_price > (deal.price || 0) && (
                <span className="text-base text-gray-400 line-through">${deal.original_price.toFixed(2)}</span>
              )}
              {deal.savings > 0 && (
                <Badge className="bg-green-100 text-green-700 text-sm ml-auto font-bold">
                  Save ${deal.savings.toFixed(2)}
                </Badge>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
              {deal.likes_count > 0 && <span>{deal.likes_count} like{deal.likes_count !== 1 ? "s" : ""}</span>}
              {deal.verify_count > 0 && <span>{deal.verify_count} verification{deal.verify_count !== 1 ? "s" : ""}</span>}
              {deal.comments_count > 0 && <span>{deal.comments_count} comment{deal.comments_count !== 1 ? "s" : ""}</span>}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-6 text-center space-y-3">
          <p className="text-sm text-gray-600 font-medium">
            Find more deals like this on Foodz Lizts
          </p>
          <Button
            className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-base font-bold rounded-xl shadow-md"
            onClick={() => window.location.href = "/"}
            data-testid="share-cta-btn"
          >
            <Download className="h-5 w-5 mr-2" />
            Get Foodz Lizts — Free for 14 Days
          </Button>
          <p className="text-xs text-gray-400">
            AI-powered grocery savings, meal plans, weekly deals & more
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-1">
          <div className="flex justify-center gap-4 text-xs text-gray-400">
            <a href="/privacy" className="underline hover:text-gray-500">Privacy</a>
            <a href="/terms" className="underline hover:text-gray-500">Terms</a>
          </div>
        </div>
      </div>
    </div>
  );
}
